import html2canvas from 'html2canvas'
import React from 'react'
import { jsPDF } from 'jspdf'
import dayjs from 'dayjs'
import { ButtonPrimary } from 'front-end/src/components'
import { OrientationEnum } from '../../graphql/public/PublicSchemaTypes'
import { getDateFormatBasedOnLocale } from 'front-end/src/components/common/date-time-pickers/dateTimePickersUtils'
import { t } from '@lingui/macro'
import IconDownload from '../../components/common/icon/IconDownload'

type DownloadCertificationAsPdfProps = {
  previewAreaRef: React.RefObject<HTMLDivElement>
  orientation: OrientationEnum
}

const A4_SMALLER_SIDE = 210
const A4_BIGGER_SIDE = 297

const getViewbox = (orientation: OrientationEnum) => {
  const isHorizontal = orientation !== OrientationEnum.HORIZONTAL

  return {
    width: isHorizontal ? A4_SMALLER_SIDE : A4_BIGGER_SIDE,
    height: isHorizontal ? A4_BIGGER_SIDE : A4_SMALLER_SIDE
  }
}
const getImageTypeFromUrl = (url: string) => {
  const match = url.match(/(jpeg|png|jpg|gif)$/i)

  if (!match || !match[1]) {
    return 'png'
  }
  return match[1].toLowerCase()
}

export const imageUrlToBase64 = async (url: string): Promise<string> => {
  const headers = new Headers()
  headers.append('pragma', 'no-cache')
  headers.append('Cache-Control', 'no-cache')
  const response = await fetch(url, {
    credentials: 'include',
    headers
  })
  const blob = await response.blob()

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = reject
    reader.onload = () => {
      resolve(reader.result as unknown as string)
    }
    reader.readAsDataURL(blob)
  })
}

const downloadAllImages = async (doc: Document) => {
  const imgs = [...doc.getElementsByTagName('image')].filter((img) => {
    return img.getAttribute('xlink:href')?.startsWith('https://')
  })

  await Promise.all(
    imgs.map(async (img) => {
      const url = img.getAttribute('xlink:href')
      if (url) {
        const b = await imageUrlToBase64(url)
        const type = getImageTypeFromUrl(url)
        img.setAttribute(
          'xlink:href',
          b.replace('data:binary/octet-stream;', `data:image/${type};`)
        )
      }
    })
  )
}

const downloadSvgAsPdf = async (
  divElement: HTMLDivElement | null,
  orientation: OrientationEnum
) => {
  if (!divElement) {
    return
  }

  const canvas = await html2canvas(divElement, {
    allowTaint: false,
    useCORS: true,
    scale: 5,
    onclone: downloadAllImages
  })
  const doc = new jsPDF({
    format: 'a4',
    compress: true,
    orientation: orientation === OrientationEnum.HORIZONTAL ? 'l' : 'p'
  })

  const { width, height } = getViewbox(orientation)
  const contentDataURL = canvas.toDataURL('image/png')
  doc.addImage(contentDataURL, 'PNG', 0, 0, width, height)
  const formatBasedOnLocale = getDateFormatBasedOnLocale()
  doc.save(`certificate_${dayjs().format(formatBasedOnLocale)}.pdf`)
}

export const DownloadCertificationAsPdf: React.FC<DownloadCertificationAsPdfProps> =
  ({ previewAreaRef, orientation }) => {
    const [disabled, setDisabled] = React.useState(false)

    const download = async () => {
      setDisabled(true)
      try {
        await downloadSvgAsPdf(previewAreaRef.current, orientation)
      } finally {
        setDisabled(false)
      }
    }

    return (
      <ButtonPrimary
        onClick={download}
        leftIcon={<IconDownload />}
        disabled={disabled}
      >
        {t`Download certificate`}
      </ButtonPrimary>
    )
  }
